import { useEffect, useRef, useState } from 'react'
import { Dialog, DialogActionsBar } from '@progress/kendo-react-dialogs'
import { FaEnvelope } from 'react-icons/fa'
import {
  emailQuote,
  getQuoteEmailPreview,
  QuoteEmailPreview,
  QuoteEmailResult,
} from '../../../data/quoteData'
import { useToast } from '../../../contexts/ToastContext'
import { getApiErrorDetails } from '../../../helpers/apiError'
import './QuoteEmailDialog.css'

const formatDateTime = (value: string) =>
  new Date(value).toLocaleString('en-GB', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })

const ccParts = (cc: string) => cc.split(/[,;\s]+/).filter((p) => p.length > 0)
// A loose check for typos while typing; the API checks each address properly.
const looksLikeEmail = (value: string) => /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(value)

interface Props {
  quoteId: number
  refFormatted: string
  onClose: () => void
  onSent: (result: QuoteEmailResult) => void
}

/**
 * Emails a quote to its contact from the shared mailbox. The wording starts
 * from a quote email template and can be changed here; the user picks which of
 * the quote's files go with it and who else is copied in.
 */
const QuoteEmailDialog = ({ quoteId, refFormatted, onClose, onSent }: Props) => {
  const { showToast } = useToast()
  const [preview, setPreview] = useState<QuoteEmailPreview | null>(null)
  const [templateId, setTemplateId] = useState<number | null>(null)
  const [subject, setSubject] = useState('')
  const [body, setBody] = useState('')
  const [cc, setCc] = useState('')
  const [fileIds, setFileIds] = useState<number[]>([])
  const [switching, setSwitching] = useState(false)
  const [sending, setSending] = useState(false)
  // Held so the load runs once per quote, whatever the parent passes.
  const closeRef = useRef(onClose)
  closeRef.current = onClose

  useEffect(() => {
    getQuoteEmailPreview(quoteId)
      .then((p) => {
        setPreview(p)
        setTemplateId(p.emailTemplateId)
        setSubject(p.subject)
        setBody(p.body)
        setFileIds(p.files.filter((f) => f.suggested).map((f) => f.documentId))
      })
      .catch((error) => {
        showToast(getApiErrorDetails(error, "The email couldn't be prepared.").message, 'error')
        closeRef.current()
      })
  }, [quoteId, showToast])

  if (!preview) return null

  const edited = subject !== preview.subject || body !== preview.body

  // Switching template replaces the wording, so check before losing changes.
  const switchTemplate = async (id: number) => {
    if (edited && !window.confirm('Switching template replaces the subject and message you have changed. Carry on?')) return
    setSwitching(true)
    try {
      const p = await getQuoteEmailPreview(quoteId, id)
      setPreview(p)
      setTemplateId(p.emailTemplateId)
      setSubject(p.subject)
      setBody(p.body)
    } catch (error) {
      showToast(getApiErrorDetails(error, "That template couldn't be loaded.").message, 'error')
    } finally {
      setSwitching(false)
    }
  }

  const toggleFile = (documentId: number) =>
    setFileIds((prev) => (prev.includes(documentId) ? prev.filter((id) => id !== documentId) : [...prev, documentId]))

  const badCc = ccParts(cc).filter((p) => !looksLikeEmail(p))

  const send = async () => {
    if (fileIds.length === 0 && !window.confirm('Nothing is attached. Send it without any files?')) return
    setSending(true)
    try {
      const result = await emailQuote(quoteId, {
        emailTemplateId: templateId,
        subject: subject.trim(),
        body,
        cc: ccParts(cc).join('; '),
        documentIds: fileIds,
      })
      showToast(`${refFormatted} emailed to ${result.emailedTo}`, 'success')
      onSent(result)
    } catch (error) {
      showToast(getApiErrorDetails(error, "The quote couldn't be emailed.").message, 'error')
    } finally {
      setSending(false)
    }
  }

  const canSend =
    !sending && !switching && !!preview.to && !preview.problem && !!subject.trim() && !!body.trim() && badCc.length === 0

  return (
    <Dialog className='qe-dialog' title={`Email ${refFormatted}`} width={620} onClose={() => !sending && onClose()}>
      <div className='qe'>
        {preview.problem ? (
          <div className='qe-banner qe-banner--error'>{preview.problem}</div>
        ) : !preview.to ? (
          <div className='qe-banner qe-banner--error'>
            The contact on this quote has no email address. Add one to the contact, or choose a different contact and
            save, then try again.
          </div>
        ) : null}

        <dl className='qe-meta'>
          <dt>From</dt>
          <dd>{preview.from || '-'}</dd>
          <dt>To</dt>
          <dd>{preview.to ? (preview.contactName ? `${preview.contactName} <${preview.to}>` : preview.to) : '-'}</dd>
        </dl>

        <label className='qe-label' htmlFor='qe-cc'>CC</label>
        <input
          id='qe-cc'
          className='qe-input'
          value={cc}
          disabled={sending}
          placeholder='Other addresses to copy in, separated by commas'
          onChange={(e) => setCc(e.target.value)}
        />
        {badCc.length > 0 && <div className='qe-field-error'>Not an email address: {badCc.join(', ')}</div>}

        {preview.templates.length > 1 && (
          <>
            <label className='qe-label' htmlFor='qe-template'>Template</label>
            <select
              id='qe-template'
              className='qe-input'
              value={templateId ?? ''}
              disabled={sending || switching}
              onChange={(e) => switchTemplate(Number(e.target.value))}
            >
              {preview.templates.map((t) => (
                <option key={t.emailTemplateId} value={t.emailTemplateId}>
                  {t.name}
                  {t.isDefault ? ' (default)' : ''}
                </option>
              ))}
            </select>
          </>
        )}

        <label className='qe-label' htmlFor='qe-subject'>Subject</label>
        <input
          id='qe-subject'
          className='qe-input'
          value={subject}
          maxLength={500}
          disabled={sending || switching}
          onChange={(e) => setSubject(e.target.value)}
        />

        <label className='qe-label' htmlFor='qe-body'>Message</label>
        <textarea
          id='qe-body'
          className='qe-input qe-body'
          value={body}
          rows={10}
          disabled={sending || switching}
          onChange={(e) => setBody(e.target.value)}
        />
        <div className='qe-hint'>
          <span>Changes here apply to this email only. Leave a blank line between paragraphs.</span>
          {edited && (
            <button
              type='button'
              className='qe-link'
              disabled={sending}
              onClick={() => {
                setSubject(preview.subject)
                setBody(preview.body)
              }}
            >
              Reset to template
            </button>
          )}
        </div>

        <div className='qe-label'>Attachments</div>
        {preview.files.length === 0 ? (
          <div className='qe-muted'>There are no files on this quote. Add the quote PDF on the File Upload tab to attach it.</div>
        ) : (
          <div className='qe-files'>
            {preview.files.map((f) => (
              <label key={f.documentId} className='qe-file'>
                <input
                  type='checkbox'
                  checked={fileIds.includes(f.documentId)}
                  disabled={sending}
                  onChange={() => toggleFile(f.documentId)}
                />
                <span>{f.fileName}</span>
              </label>
            ))}
          </div>
        )}

        {preview.emailedDate && (
          <p className='qe-resend'>
            Already emailed {formatDateTime(preview.emailedDate)}
            {preview.emailedTo ? ` to ${preview.emailedTo}` : ''}
            {preview.emailedCc ? `, copied to ${preview.emailedCc}` : ''}
            {preview.emailedBy ? ` by ${preview.emailedBy}` : ''}. Sending again will email it again.
          </p>
        )}
      </div>
      <DialogActionsBar>
        <button type='button' className='qe-btn qe-btn--secondary' onClick={onClose} disabled={sending}>
          Cancel
        </button>
        <button type='button' className='qe-btn qe-btn--primary' onClick={send} disabled={!canSend}>
          <FaEnvelope />
          {sending ? 'Sending…' : 'Send'}
        </button>
      </DialogActionsBar>
    </Dialog>
  )
}

export default QuoteEmailDialog
